import React, { Component } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
};

const lastMonthKey = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return monthKey(d);
};

class AdminMonthlyBudgetReportPage extends Component {
  state = {
    primaryMonth: lastMonthKey(),
    compareEnabled: false,
    compareMonth: "",

    months: [],
    platforms: [],
    data: [],
    loading: false,
    dataFetched: false,
  };

  componentDidMount() {
    this.fetchData();
  }

  toggleCompare = () => {
    this.setState((prev) => ({ compareEnabled: !prev.compareEnabled }));
  };

  fetchData = async () => {
    const { primaryMonth, compareEnabled, compareMonth } = this.state;
    if (!primaryMonth) { toast.error("Select a month"); return; }

    const monthList = [primaryMonth];
    if (compareEnabled && compareMonth && compareMonth !== primaryMonth) monthList.push(compareMonth);

    this.setState({ loading: true });
    try {
      const res = await api.get("/admin/marketing/monthly-report", { params: { months: monthList.join(",") } });
      if (res.data.success) {
        const { months, platforms, data } = res.data.data;
        this.setState({ months, platforms, data, dataFetched: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching monthly report");
    } finally {
      this.setState({ loading: false });
    }
  };

  cellFor = (platformId, month) => {
    const row = this.state.data.find((d) => d.platform_id === platformId && d.month === month);
    return row || { budget: 0, spent: 0 };
  };

  render() {
    const { primaryMonth, compareEnabled, compareMonth, months, platforms, loading, dataFetched } = this.state;

    const totalsByMonth = {};
    months.forEach((m) => {
      totalsByMonth[m] = platforms.reduce(
        (acc, p) => {
          const cell = this.cellFor(p.id, m);
          acc.budget += Number(cell.budget || 0);
          acc.spent += Number(cell.spent || 0);
          return acc;
        },
        { budget: 0, spent: 0 }
      );
    });

    const grandTotal = Object.values(totalsByMonth).reduce(
      (acc, t) => ({ budget: acc.budget + t.budget, spent: acc.spent + t.spent }),
      { budget: 0, spent: 0 }
    );

    return (
      <div className="adm-page">
        <div className="adm-page-inner">
          <div className="adm-header">
            <div>
              <h4 className="adm-header-title">
                <span className="adm-icon-badge"><i className="fas fa-calendar-alt"></i></span>
                Monthly Budget/Spent Report
              </h4>
              <p className="adm-header-sub">Platform-wise total budget and spent for any month, with an optional month to compare against.</p>
            </div>
            {dataFetched && platforms.length > 0 && (
              <div className="adm-toolbar">
                <span className="adm-badge adm-badge-primary"><i className="fas fa-wallet"></i>₹ {grandTotal.budget.toFixed(0)} budget</span>
                <span className="adm-badge adm-badge-muted"><i className="fas fa-receipt"></i>₹ {grandTotal.spent.toFixed(0)} spent</span>
              </div>
            )}
          </div>

          <div className="adm-card">
            <div className="adm-card-body">
              <div className="adm-filter-bar">
                <div className="adm-filter-group">
                  <label>Month</label>
                  <input
                    type="month"
                    value={primaryMonth}
                    onChange={(e) => this.setState({ primaryMonth: e.target.value })}
                  />
                </div>

                <div className="adm-filter-group">
                  <label style={{ visibility: "hidden" }}>Compare</label>
                  <label style={{ display: "flex", alignItems: "center", gap: "7px", height: "38px", fontWeight: 500, fontSize: "13px", color: "var(--adm-text)", cursor: "pointer" }}>
                    <input type="checkbox" checked={compareEnabled} onChange={this.toggleCompare} style={{ accentColor: "var(--adm-primary)" }} />
                    Compare with another month
                  </label>
                </div>

                {compareEnabled && (
                  <div className="adm-filter-group">
                    <label>Compare With</label>
                    <input
                      type="month"
                      value={compareMonth}
                      onChange={(e) => this.setState({ compareMonth: e.target.value })}
                    />
                  </div>
                )}

                <div className="adm-filter-group">
                  <label style={{ visibility: "hidden" }}>Go</label>
                  <button className="adm-btn adm-btn-primary" onClick={this.fetchData} disabled={loading}>
                    {loading ? (
                      <><i className="fas fa-spinner fa-spin"></i>Loading...</>
                    ) : (
                      <><i className="fas fa-play"></i>Generate Report</>
                    )}
                  </button>
                </div>
              </div>
              {compareEnabled && !compareMonth && (
                <small className="text-muted d-block mt-2">Pick a month to compare against, then generate.</small>
              )}
            </div>
          </div>

          {dataFetched && (
            <div className="adm-card">
              <div className="adm-card-body">
                {platforms.length === 0 ? (
                  <div className="adm-empty">
                    <i className="fas fa-inbox"></i>
                    <strong>No platforms found</strong>
                    <span>Add a platform under Campaigns &rarr; Manage Platforms first.</span>
                  </div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>Platform</th>
                          {months.map((m) => (
                            <th key={m} className="center" colSpan={2}>{monthLabel(m)}</th>
                          ))}
                        </tr>
                        <tr>
                          <th></th>
                          {months.map((m) => (
                            <React.Fragment key={m}>
                              <th className="center" style={{ fontSize: "10px" }}>Budget</th>
                              <th className="center" style={{ fontSize: "10px" }}>Spent</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {platforms.map((p) => (
                          <tr key={p.id}>
                            <td style={{ fontWeight: 700 }}>{p.name}</td>
                            {months.map((m) => {
                              const cell = this.cellFor(p.id, m);
                              return (
                                <React.Fragment key={m}>
                                  <td className="center adm-cell-budget">₹ {Number(cell.budget || 0).toFixed(2)}</td>
                                  <td className="center adm-cell-spent">₹ {Number(cell.spent || 0).toFixed(2)}</td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        ))}
                        <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                          <td>&Sigma; TOTAL</td>
                          {months.map((m) => (
                            <React.Fragment key={m}>
                              <td className="center adm-cell-budget">₹ {totalsByMonth[m].budget.toFixed(2)}</td>
                              <td className="center adm-cell-spent">₹ {totalsByMonth[m].spent.toFixed(2)}</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default AdminMonthlyBudgetReportPage;
